import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setCsrfToken } from '../api/client';
import { AuthContext } from './AuthContext';
import type { Me } from './roles';

/**
 * /api/users/me is the single source of truth for identity, role, status and MFA state —
 * re-fetched on boot and after every auth transition. Nothing is trusted from local storage.
 * Every identity transition clears the query cache — cached data belongs to a user, and
 * serving one account's bookings/teams to the next would otherwise persist until staleness.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // Mirrors `user` for the unauthorised listener: several in-flight queries 401-ing together
  // fire the event once each — the ref makes every firing after the first a no-op.
  const userRef = useRef<Me | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const me = await api<Me>('/api/users/me');
      setCsrfToken(me.csrfToken);
      userRef.current = me; // assigned at source — the effect alone leaves a pre-commit window
      setUser(me);
    } catch {
      userRef.current = null;
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // CSRF first so login (a mutating call) works pre-session; /me replaces it when signed in.
      try {
        const { token } = await api<{ token: string }>('/api/auth/csrf');
        setCsrfToken(token);
      } catch {
        /* API unreachable — login will surface the error */
      }
      if (!cancelled) {
        await refresh();
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    const onUnauthorised = () => {
      if (!userRef.current) {
        return; // already signed out — a burst of 401s must not clear repeatedly
      }
      userRef.current = null;
      setUser(null);
      queryClient.clear();
    };
    window.addEventListener('pb:unauthorised', onUnauthorised);
    return () => window.removeEventListener('pb:unauthorised', onUnauthorised);
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api<{ step: 'done' | 'mfa' }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      // Clear on BOTH steps: a successful password check means an identity transition has
      // begun even when TOTP is still pending — no prior account's cache may cross it.
      queryClient.clear();
      if (result.step === 'done') {
        await refresh();
      }
      return result.step;
    },
    [refresh, queryClient],
  );

  const verifyMfa = useCallback(
    async (code: string, isRecoveryCode = false) => {
      await api('/api/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ code, isRecoveryCode }),
      });
      queryClient.clear();
      await refresh();
    },
    [refresh, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch {
      // The server never confirmed the sign-out, so the cookie session may still be alive.
      // Showing a signed-out UI over a live session would be a lie on a shared machine —
      // reload so the true cookie state is re-detected instead.
      window.location.reload();
      return;
    }

    userRef.current = null;
    setUser(null);
    queryClient.clear();
    // Antiforgery tokens embed the identity — the signed-in token won't validate for the
    // next (anonymous) login POST, so fetch a fresh one.
    try {
      const { token } = await api<{ token: string }>('/api/auth/csrf');
      setCsrfToken(token);
    } catch {
      /* next page load re-primes */
    }
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
